import React, { useState, useContext, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import Avatar from '../components/Avatar';
import ApprovalActionModal from '../components/ApprovalActionModal';
import { generateEmailSummary } from '../utils/aiSummarizer';
import { Bell, Calendar, Database, Mail, Plus, ArrowRight, UserCheck, ShieldAlert, Check, X, FileText, Edit2, Trash2, ChevronDown, ChevronRight, Smartphone, MessageSquare, Clock, Receipt } from 'lucide-react';

const DashboardTab = ({ setActiveTab }) => {
  const { 
    currentUser, 
    emails, 
    users, 
    approvals, 
    contracts,
    handleApprovalAction,
    sendEmail,
    getUserStorageUsage,
    editNotice,
    deleteEmails,
    kakaoBizConfig,
    sendKakaoBizMessage,
    formatKakaoTemplate,
    getAutoApprovalLine,
    groupMails,
    blockedSenders
  } = useContext(AppContext);

  // Selected approval for quick action modal
  const [selectedApprovalForAction, setSelectedApprovalForAction] = useState(null);

  // Approvals in which current user is in the approval line
  const pendingApprovalsForMe = useMemo(() => {
    if (!currentUser || !approvals) return [];
    const myId = currentUser.UserID;
    const myName = currentUser.Name;
    const myEmail = (currentUser.Email || '').toLowerCase();

    return approvals
      .filter(a => a.Status === 'PENDING' || a.Status === 'CANCEL_REQUESTED')
      .map(a => {
        const applicantObj = users?.find(u => u.UserID === a.ApplicantID) || { UserID: a.ApplicantID, Department: a.ApplicantDept };
        const stages = (a.ApprovalStages && a.ApprovalStages.length > 0)
          ? a.ApprovalStages
          : (typeof getAutoApprovalLine === 'function' ? getAutoApprovalLine(applicantObj) : []);

        const currentStep = Number(a.CurrentStep || 1);
        const currentStage = stages.find(s => Number(s.Step) === currentStep);
        const currentApproverName = currentStage?.ApproverName || currentStage?.RoleName || `${currentStep}차 결재자`;

        // Is current user in the approval line? (Strict check: must be in stages)
        const isInLine = stages.some(s => 
          s.ApproverID === myId || 
          s.ApproverName === myName || 
          (s.ApproverEmail && s.ApproverEmail.toLowerCase() === myEmail)
        );

        if (!isInLine) return null;

        // Is it strictly current user's turn?
        const isMyTurn = currentStage && (
          currentStage.ApproverID === myId ||
          currentStage.ApproverName === myName ||
          (currentStage.ApproverEmail && currentStage.ApproverEmail.toLowerCase() === myEmail)
        );

        return {
          ...a,
          calculatedStages: stages,
          currentStep,
          currentApproverName,
          isMyTurn: Boolean(isMyTurn)
        };
      })
      .filter(Boolean);
  }, [approvals, currentUser, users, getAutoApprovalLine]);

  const myTurnCount = pendingApprovalsForMe.filter(a => a.isMyTurn).length;

  const myPendingCount = (contracts || []).filter(c => {
    if (!currentUser) return false;
    const myEmail = (currentUser.Email || '').toLowerCase();
    const myId = currentUser.UserID;
    const myName = currentUser.Name;
    const isMe = (c.UserID && c.UserID === myId) || (c.UserEmail && c.UserEmail.toLowerCase() === myEmail) || (c.UserName === myName);
    return isMe && c.Status === 'PENDING';
  }).length;

  // Notice creation modal state
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [noticeTitle, setNoticeTitle] = useState('');
  const [noticeBody, setNoticeBody] = useState('');
  const [isSubmittingNotice, setIsSubmittingNotice] = useState(false);

  // KakaoTalk notification simulation states
  const [sendKakao, setSendKakao] = useState(false);
  const [showKakaoPreview, setShowKakaoPreview] = useState(false);
  const [kakaoPreviewData, setKakaoPreviewData] = useState(null);

  // Collapsed months state for tree layout
  const [collapsedMonths, setCollapsedMonths] = useState({});

  // Notice edit modal state
  const [editingNotice, setEditingNotice] = useState(null);
  const [editNoticeTitle, setEditNoticeTitle] = useState('');
  const [editNoticeBody, setEditNoticeBody] = useState('');

  // Find target user details in users list for leaves & storage
  const userDetail = users.find(u => u.UserID === currentUser?.UserID) || currentUser;

  // Filter notices from emails that start with [공지]
  const notices = emails
    .filter(e => (e.Subject || '').trim().startsWith('[공지]'))
    .map(e => ({
      id: e.EmailID,
      title: e.Subject.replace('[공지]', '').trim(),
      body: e.BodyText || '',
      date: new Date(e.Timestamp || e.DeliveredAt).toLocaleDateString(),
      rawDate: e.Timestamp || e.DeliveredAt,
      sender: e.Sender || '인사팀'
    }));

  // Group notices by Month (e.g. "2026년 08월")
  const groupedNotices = notices.reduce((acc, n) => {
    const d = new Date(n.rawDate || Date.now());
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const monthKey = `${year}년 ${month}월`;
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(n);
    return acc;
  }, {});

  // Sort months descending (e.g. 2026년 08월 first, then 2026년 07월)
  const sortedMonthKeys = Object.keys(groupedNotices).sort((a, b) => b.localeCompare(a));

  // Filter inbox emails for current user (Aliases & OwnerEmail included, strictly inbox)
  const userInboxEmails = useMemo(() => {
    if (!currentUser || !emails) return [];
    const raw = (currentUser?.Email || '').toLowerCase().trim();
    const aliasList = (currentUser?.Aliases || '').toLowerCase().split(',').map(a => a.trim()).filter(Boolean);
    const allUserAddresses = [raw, ...aliasList].filter(Boolean);
    const blocked = (blockedSenders || []).map(b => (b || '').toLowerCase().trim());

    let trashMap = {};
    try {
      const savedTrash = localStorage.getItem('clou_trash_emails_v1');
      if (savedTrash) trashMap = JSON.parse(savedTrash);
    } catch (e) {
      console.warn('Dashboard failed to parse trash map:', e);
    }

    return emails.filter(e => {
      if (!e) return false;
      // 1. Trashed or Spam check
      const isTrashed = Boolean(trashMap[e.EmailID]) || e.Folder === 'trash' || e.IsDeleted === true;
      if (isTrashed) return false;
      if (e.IsSpam || e.Folder === 'spam') return false;
      const sender = (e.Sender || '').toLowerCase();
      if (blocked.some(b => b && sender.includes(b))) return false;

      // 2. Sent email check (exclude pure sent emails from inbox)
      const isMeSender = allUserAddresses.some(a => sender.includes(a));
      if ((e.Folder === 'sent' || isMeSender) && !e.EmailID?.includes('-inbox')) {
        const rec = (e.Recipient || '').toLowerCase();
        const isSelf = allUserAddresses.some(a => rec.includes(a));
        if (!isSelf) return false;
      }

      // 3. Must be addressed to me, my alias, or broadcast
      const rec = (e.Recipient || '').toLowerCase();
      const cc = (e.Cc || '').toLowerCase();
      const bcc = (e.Bcc || '').toLowerCase();
      const owner = (e.OwnerEmail || '').toLowerCase().trim();

      const isMeRecipient = allUserAddresses.some(a => rec.includes(a) || cc.includes(a) || bcc.includes(a) || owner.includes(a)) ||
                            rec.includes('all@clouflake.com') || cc.includes('all@clouflake.com');

      if (!isMeRecipient) {
        if (Array.isArray(groupMails)) {
          const isAssigned = groupMails.some(group => {
            const groupAddr = (group.GroupEmail || '').toLowerCase();
            if (!rec.includes(groupAddr) && !cc.includes(groupAddr)) return false;
            const assignees = (group.Assignees || []).map(a => a.toLowerCase().trim());
            return allUserAddresses.some(a => assignees.includes(a));
          });
          if (!isAssigned) return false;
        } else {
          return false;
        }
      }

      return true;
    });
  }, [emails, currentUser, blockedSenders, groupMails]);

  const unreadMails = userInboxEmails.filter(e => !e.IsRead).slice(0, 3);
  const unreadCount = userInboxEmails.filter(e => !e.IsRead).length;

  // Latest received email strictly in Received Mailbox (inbox)
  const latestMail = useMemo(() => {
    if (!userInboxEmails || userInboxEmails.length === 0) return null;
    const sorted = [...userInboxEmails].sort((a, b) => new Date(b.Timestamp || b.DeliveredAt || 0) - new Date(a.Timestamp || a.DeliveredAt || 0));
    const firstUnread = sorted.find(e => !e.IsRead);
    return firstUnread || sorted[0];
  }, [userInboxEmails]);

  const latestMailSummary = useMemo(() => {
    if (!latestMail) return null;
    return generateEmailSummary(latestMail);
  }, [latestMail]);

  // Pending approvals matching current user's approval line step (Requirement 3)
  const isUserApprover = (userId) => {
    // Check if user is a lead or designated in stage
    return true; // We filter stages dynamically below
  };

  const myPendingApprovals = approvals.filter(appr => {
    if (appr.Status !== 'PENDING' && appr.Status !== 'CANCEL_REQUESTED') return false;

    if (appr.Status === 'CANCEL_REQUESTED') {
      if (currentUser?.Role === 'ADMIN') return true;
      return appr.ApprovalStages?.some(s => s.ApproverID === currentUser?.UserID || s.ApproverName === currentUser?.Name);
    }

    const currentStep = appr.CurrentStep || 1;
    const currentStage = appr.ApprovalStages?.find(s => s.Step === currentStep);
    
    // Check if current stage approver is the logged-in user
    // We match by name or ID
    if (!currentStage) return false;
    const matchedUserId = currentStage.ApproverID;
    
    // Fallback: match by ApproverName
    const nameMatch = currentStage.ApproverName === currentUser?.Name;
    const idMatch = matchedUserId === currentUser?.UserID;
    return (idMatch || nameMatch) && currentStage.Status === 'PENDING';
  });

  const handlePostNotice = async (e) => {
    e.preventDefault();
    if (!noticeTitle || !noticeBody) {
      alert('공지사항 제목과 내용을 입력해 주세요.');
      return;
    }

    setIsSubmittingNotice(true);
    try {
      // Send notice as email to all@clouflake.com
      await sendEmail(
        'all@clouflake.com',
        `[공지] ${noticeTitle}`,
        noticeBody,
        noticeBody,
        [],
        currentUser.Email
      );

      // Automated KakaoTalk dispatch to all active employees
      const shouldSendKakao = sendKakao || (kakaoBizConfig?.enabled && kakaoBizConfig?.autoNoticeEnabled);
      let kakaoCount = 0;

      if (shouldSendKakao && typeof sendKakaoBizMessage === 'function') {
        const eligibleUsers = (users || []).filter(u => {
          const cleanPhone = (u.Phone || '').replace(/[^0-9]/g, '');
          return u.EmploymentStatus !== '퇴사' && 
                 cleanPhone.length >= 10 && 
                 u.KakaoNoticeEnabled !== false;
        });

        // Deduplicate recipients by phone
        const uniqueRecipients = Array.from(new Map(eligibleUsers.map(u => [u.Phone.replace(/[^0-9]/g, ''), u])).values());

        const formatted = formatKakaoTemplate ? formatKakaoTemplate('NOTICE_BROADCAST', {
          공지제목: noticeTitle,
          작성자명: currentUser?.Name || '관리자',
          등록일시: new Date().toLocaleDateString('ko-KR') + ' ' + new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
          공지내용: noticeBody
        }) : { title: `[공지] ${noticeTitle}`, messageText: noticeBody, buttonText: '공지 확인하기', buttonUrl: `${window.location.origin}/#/dashboard` };

        for (const emp of uniqueRecipients) {
          try {
            await sendKakaoBizMessage({
              recipientPhone: emp.Phone,
              recipientName: emp.Name,
              recipientEmail: emp.Email,
              title: formatted.title,
              messageText: formatted.messageText,
              buttonText: formatted.buttonText || '공지 확인하기',
              buttonUrl: formatted.buttonUrl,
              messageType: 'NOTICE'
            });
            kakaoCount++;
          } catch (kErr) {
            console.warn('[Dashboard Kakao Notice Warning]', kErr);
          }
        }
      }
      
      if (sendKakao) {
        setKakaoPreviewData({
          title: noticeTitle,
          body: noticeBody,
          sender: `${userDetail?.Name || currentUser.Name} ${userDetail?.JobTitle || userDetail?.Position || '관리자'}`,
          timestamp: new Date().toLocaleString('ko-KR', { hour12: false })
        });
        setShowKakaoPreview(true);
      }

      setNoticeTitle('');
      setNoticeBody('');
      setSendKakao(false);
      setShowNoticeModal(false);

      if (!sendKakao) {
        alert(`공지사항이 성공적으로 등록되었습니다.${kakaoCount > 0 ? ` (임직원 ${kakaoCount}명에게 카카오톡 알림 전송 완료)` : ''}`);
      }
    } catch (err) {
      console.error(err);
      alert('공지사항 등록에 실패했습니다.');
    } finally {
      setIsSubmittingNotice(false);
    }
  };

  const handleEditNoticeSubmit = async (e) => {
    e.preventDefault();
    if (!editNoticeTitle || !editNoticeBody) {
      alert('공지사항 제목과 내용을 입력해 주세요.');
      return;
    }
    try {
      await editNotice(editingNotice.id, editNoticeTitle, editNoticeBody);
      setEditingNotice(null);
      alert('공지사항이 성공적으로 수정되었습니다.');
    } catch (err) {
      console.error(err);
      alert('공지사항 수정에 실패했습니다.');
    }
  };

  const handleDeleteNoticeClick = async (noticeId) => {
    if (window.confirm('이 공지사항을 정말 삭제하시겠습니까?')) {
      try {
        await deleteEmails([noticeId]);
        alert('공지사항이 성공적으로 삭제되었습니다.');
      } catch (err) {
        console.error(err);
        alert('공지사항 삭제에 실패했습니다.');
      }
    }
  };

  // Check if current user has an approved resignation
  const myApprovedResignation = (contracts || []).find(c => {
    if (!currentUser) return false;
    const myId = currentUser.UserID;
    const myEmail = (currentUser.Email || '').toLowerCase();
    const myName = currentUser.Name;
    const isMine = (c.UserID && c.UserID === myId) || 
                   (c.UserEmail && c.UserEmail.toLowerCase() === myEmail) || 
                   (c.UserName === myName);
    const isResig = Boolean(c.IsResignation || c.ContractType === 'RESIGNATION' || c.ContractType === 'RESIGNATION_NDA');
    const isApproved = c.Status === 'APPROVED' || c.Status === 'SIGNED' || c.ResignationStatus === 'APPROVED';
    return isMine && isResig && isApproved;
  });

  const resignationDDayInfo = (() => {
    if (!myApprovedResignation) return null;
    const resigDateStr = myApprovedResignation.ResignationDate || myApprovedResignation.ContractEndDate;
    if (!resigDateStr) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(resigDateStr);
    target.setHours(0, 0, 0, 0);

    const diffTime = target.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let label = '';
    let badgeClass = '';
    let subMessage = '';

    if (diffDays > 0) {
      label = `D-${diffDays}`;
      badgeClass = 'bg-rose-500 text-white';
      subMessage = `퇴사 예정일(${resigDateStr})까지 ${diffDays}일 남았습니다. 인수인계서 작성 및 자산 반납 준비를 진행해 주세요.`;
    } else if (diffDays === 0) {
      label = 'D-Day';
      badgeClass = 'bg-rose-600 text-white animate-pulse';
      subMessage = `오늘(${resigDateStr})이 최종 퇴사일입니다. 인수인계 확인 및 보안 서약 서명을 완료해 주세요.`;
    } else {
      label = `D+${Math.abs(diffDays)}`;
      badgeClass = 'bg-slate-600 text-white';
      subMessage = `퇴사일(${resigDateStr})이 경과하였습니다. 완료된 사직 서류는 전자계약 보관함에서 상시 열람할 수 있습니다.`;
    }

    return {
      resignationDate: resigDateStr,
      diffDays,
      dDayLabel: label,
      badgeClass,
      subMessage,
      contract: myApprovedResignation
    };
  })();

  // Calculate storage quota bar progress using real user storage usage
  const storageInfo = getUserStorageUsage ? getUserStorageUsage(currentUser?.UserID) : { usedMB: 1.25, quotaGB: 10, usagePercent: 0.1 };

  return (
    <div className="p-4 md:p-8 space-y-6 md:space-y-8 max-w-7xl mx-auto text-left">
      {/* 0. Resignation D-Day Guidance Banner (Pinned for Approved Resignations) */}
      {resignationDDayInfo && (
        <div className="bg-gradient-to-r from-rose-900 via-slate-900 to-rose-950 text-white rounded-3xl p-5 md:p-6 shadow-lg border border-rose-500/30 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-rose-500/20 text-rose-300 rounded-2xl border border-rose-400/30 flex-shrink-0 mt-0.5">
              <UserCheck className="w-6 h-6 text-rose-400" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-black tracking-wider ${resignationDDayInfo.badgeClass}`}>
                  {resignationDDayInfo.dDayLabel}
                </span>
                <span className="text-xs font-bold text-rose-300">
                  사직원 4단계 대표이사 최종 승인 완료
                </span>
                <span className="text-[11px] text-slate-400 font-medium">
                  (퇴사 예정일: {resignationDDayInfo.resignationDate})
                </span>
              </div>
              <h3 className="text-base md:text-lg font-black tracking-tight text-white">
                {currentUser?.Name}님의 사직원(퇴직원) 결재가 최종 승인되었습니다.
              </h3>
              <p className="text-xs text-rose-200/90 font-medium">
                {resignationDDayInfo.subMessage}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 self-end md:self-center flex-shrink-0">
            <button
              type="button"
              onClick={() => setActiveTab('contract')}
              className="px-4 py-2.5 bg-rose-600 hover:bg-rose-500 text-white font-black text-xs rounded-xl shadow-xs transition-all cursor-pointer inline-flex items-center gap-1.5"
            >
              <FileText className="w-4 h-4" />
              <span>전자계약 보관함 바로가기</span>
            </button>
          </div>
        </div>
      )}

      {/* 1. Dashboard Welcome Banner */}
      <div className="bg-indigo-900 text-white rounded-3xl p-6 md:p-8 shadow-md relative overflow-hidden flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="flex items-center gap-5 relative z-10">
          <Avatar config={userDetail.AvatarConfig} size={70} className="border-2 border-indigo-400/50 flex-shrink-0" />
          <div className="space-y-1.5">
            <span className="px-3 py-1 bg-indigo-700/50 backdrop-blur-xs rounded-full text-[10px] font-bold tracking-wider text-indigo-200">
              CLOUFLAKE PORTAL
            </span>
            <h1 className="text-xl md:text-3xl font-extrabold tracking-tight">
              반갑습니다, {userDetail.Name} {userDetail.JobTitle || userDetail.Position || '사원'}님!
            </h1>
            <p className="text-xs md:text-sm text-indigo-200/90 max-w-xl font-medium">
              오늘도 즐겁고 보람찬 하루 되세요. 아래 대시보드에서 사내 공지 및 나의 연차, 메일 현황을 한눈에 확인할 수 있습니다.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 relative z-10">
          <button 
            onClick={() => setActiveTab('webmail')}
            className="px-5 py-3 bg-white text-indigo-900 hover:bg-indigo-50 font-bold text-sm rounded-xl transition-all cursor-pointer inline-flex items-center gap-2 shadow-sm"
          >
            <Mail className="w-4 h-4" />
            <span>메일함 열기</span>
          </button>
          {currentUser.Role === 'ADMIN' && (
            <button 
              onClick={() => setShowNoticeModal(true)}
              className="px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm rounded-xl transition-all cursor-pointer inline-flex items-center gap-2 shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span>공지 작성</span>
            </button>
          )}
        </div>
        <div className="absolute right-0 bottom-0 top-0 w-1/3 bg-radial-gradient from-indigo-500/20 to-transparent pointer-events-none" />
      </div>

      {/* 2. Stat Widgets Grid (4 Columns) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {/* Widget: Unread Emails */}
        <div className="bg-white border border-slate-200 rounded-3xl p-5 md:p-6 shadow-xs flex items-center justify-between gap-4">
          <div className="space-y-1 text-left">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">안읽은 메일</span>
            <span className="text-2xl md:text-3xl font-extrabold text-indigo-700 block">{unreadCount} <span className="text-sm font-semibold text-slate-500">건</span></span>
            <button 
              onClick={() => setActiveTab('webmail')}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 mt-2 cursor-pointer"
            >
              <span>메일함 바로가기</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="p-3.5 bg-indigo-50 text-indigo-600 rounded-2xl">
            <Mail className="w-6 h-6 md:w-7 md:h-7" />
          </div>
        </div>

        {/* Widget: Remaining Leave */}
        <div className="bg-white border border-slate-200 rounded-3xl p-5 md:p-6 shadow-xs flex items-center justify-between gap-4">
          <div className="space-y-1 text-left">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">잔여 연차</span>
            <span className="text-2xl md:text-3xl font-extrabold text-emerald-600 block">
              {userDetail.RemainingLeave} <span className="text-sm font-semibold text-slate-500">/ {userDetail.TotalLeave} 일</span>
            </span>
            <button 
              onClick={() => setActiveTab('leave')}
              className="text-xs font-bold text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1 mt-2 cursor-pointer"
            >
              <span>연차 신청 바로가기</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="p-3.5 bg-emerald-50 text-emerald-600 rounded-2xl">
            <Calendar className="w-6 h-6 md:w-7 md:h-7" />
          </div>
        </div>

        {/* Widget: Electronic Contracts */}
        <div className="bg-white border border-slate-200 rounded-3xl p-5 md:p-6 shadow-xs flex items-center justify-between gap-4">
          <div className="space-y-1 text-left">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">전자계약</span>
            <span className="text-2xl md:text-3xl font-extrabold text-slate-900 block">
              {myPendingCount > 0 ? (
                <span className="text-amber-600">{myPendingCount} <span className="text-sm font-semibold text-amber-700">건 대기</span></span>
              ) : (
                <span className="text-emerald-600 text-xl md:text-2xl">체결 완료</span>
              )}
            </span>
            <button 
              onClick={() => setActiveTab('contract')}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 mt-2 cursor-pointer"
            >
              <span>{myPendingCount > 0 ? '지금 서명하기' : '계약서 보관함'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className={`p-3.5 rounded-2xl ${myPendingCount > 0 ? 'bg-amber-50 text-amber-600 animate-pulse' : 'bg-indigo-50 text-indigo-600'}`}>
            <FileText className="w-6 h-6 md:w-7 md:h-7" />
          </div>
        </div>

        {/* Widget: Storage usage */}
        <div className="bg-white border border-slate-200 rounded-3xl p-5 md:p-6 shadow-xs flex flex-col justify-between gap-3">
          <div className="flex items-center justify-between w-full">
            <div className="space-y-0.5 text-left">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">사용 용량</span>
              <span className="text-xl md:text-2xl font-extrabold text-slate-800 block">
                {storageInfo.usedMB} MB <span className="text-xs font-semibold text-slate-500">/ {storageInfo.quotaGB} GB</span>
              </span>
            </div>
            <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
              <Database className="w-5 h-5 md:w-6 md:h-6" />
            </div>
          </div>
          <div className="space-y-1">
            <div className="w-full bg-slate-100 rounded-full h-1.5">
              <div 
                className="bg-amber-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.max(storageInfo.usagePercent, 2)}%` }}
              />
            </div>
            <div className="flex justify-between items-center text-[10px] font-bold text-slate-500">
              <span>{storageInfo.usagePercent}% 사용 중</span>
            </div>
          </div>
        </div>
      </div>

      {/* 2.5 AI Smart Summary Cards (CLOU Design System Pastel Trio) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Blue: AI Mail Summary */}
        <div 
          onClick={() => setActiveTab('webmail')}
          className="bg-[#F6F8FF] rounded-3xl p-5 border border-indigo-100/90 shadow-2xs hover:shadow-md transition-all cursor-pointer space-y-2.5 group text-left flex flex-col justify-between"
        >
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[#5C5FEF] font-extrabold text-xs flex items-center gap-2">
                <img 
                  src="/assets/ai_robot.jpg" 
                  alt="AI Robot" 
                  className="w-6 h-6 rounded-full object-cover shadow-2xs border border-indigo-200 inline-block flex-shrink-0" 
                />
                <span>AI 최신 메일 요약 브리핑</span>
              </span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                latestMail && !latestMail.IsRead 
                  ? 'bg-rose-50 text-rose-600 border-rose-200 animate-pulse font-extrabold' 
                  : 'bg-white text-indigo-500 border-indigo-100'
              }`}>
                {latestMail && !latestMail.IsRead ? '신규 수신' : unreadCount > 0 ? `안읽음 ${unreadCount}건` : '최신 요약'}
              </span>
            </div>

            {latestMailSummary ? (
              <div className="space-y-1.5 pt-0.5">
                <div className="flex items-center gap-1.5 text-slate-800 font-extrabold text-xs truncate">
                  <span className="text-indigo-600 flex-shrink-0">[{latestMailSummary.sender?.split('<')[0]?.trim() || '발신자'}]</span>
                  <span className="truncate">{latestMailSummary.subject}</span>
                </div>
                <div className="text-[11px] text-slate-600 space-y-1 bg-white/80 p-2.5 rounded-2xl border border-indigo-100/70 leading-relaxed font-medium">
                  <p className="line-clamp-2">
                    <strong className="text-slate-800">📌 안건:</strong> {latestMailSummary.agenda}
                  </p>
                  {latestMailSummary.actionItem && (
                    <p className="line-clamp-1 text-slate-700">
                      <strong className="text-indigo-600">⚡ 요청:</strong> {latestMailSummary.actionItem}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500 font-medium py-2">
                수신된 메일이 없습니다. 새로운 업무 메일이 도착하면 실시간으로 AI 요약해 드립니다.
              </p>
            )}
          </div>

          <div className="text-[11px] font-extrabold text-[#5C5FEF] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 pt-0.5">
            <span>메일함 열기</span>
            <span>➔</span>
          </div>
        </div>

        {/* Green: Action Items / Leaves */}
        <div 
          onClick={() => setActiveTab(pendingApprovalsForMe.length > 0 ? 'approval' : 'leave')}
          className="bg-[#F4FAF7] rounded-3xl p-5 border border-emerald-100/90 shadow-2xs hover:shadow-md transition-all cursor-pointer space-y-3 group text-left"
        >
          <div className="flex items-center justify-between">
            <span className="text-emerald-700 font-extrabold text-xs flex items-center gap-1.5">
              <span>✅</span> 업무 결재 & 할 일
            </span>
            <span className="text-[10px] font-bold text-emerald-700 bg-white px-2 py-0.5 rounded-full border border-emerald-100">
              {pendingApprovalsForMe.length}건 대기
            </span>
          </div>
          <p className="text-xs text-slate-700 font-semibold leading-relaxed">
            {pendingApprovalsForMe.length > 0 
              ? `결재 대기 중인 문서가 ${pendingApprovalsForMe.length}건 있습니다. 지연 없이 승인/반려를 진행하세요.` 
              : `내 차례 결재 문서가 없습니다. 실질 잔여 연차는 ${userDetail.RemainingLeave}일입니다.`}
          </p>
          <div className="text-[11px] font-extrabold text-emerald-700 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
            <span>{pendingApprovalsForMe.length > 0 ? '결재 승인하기' : '연차 캘린더 보기'}</span>
            <span>➔</span>
          </div>
        </div>

        {/* Purple: Project Insight */}
        <div 
          onClick={() => setActiveTab('contract')}
          className="bg-[#F9F6FE] rounded-3xl p-5 border border-purple-100/90 shadow-2xs hover:shadow-md transition-all cursor-pointer space-y-3 group text-left"
        >
          <div className="flex items-center justify-between">
            <span className="text-purple-700 font-extrabold text-xs flex items-center gap-1.5">
              <span>💡</span> AX 전자계약 & 보안
            </span>
            <span className="text-[10px] font-bold text-purple-700 bg-white px-2 py-0.5 rounded-full border border-purple-100">
              {myPendingCount > 0 ? `${myPendingCount}건 서명대기` : '안전 체결'}
            </span>
          </div>
          <p className="text-xs text-slate-700 font-semibold leading-relaxed">
            {myPendingCount > 0 
              ? `서명이 필요한 전자계약서 ${myPendingCount}건이 대기 중입니다. 지금 바로 서명을 완료하세요.` 
              : '체결된 모든 계약서와 보안 서약이 클라우드 보안 보관함에 암호화 보관 중입니다.'}
          </p>
          <div className="text-[11px] font-extrabold text-purple-700 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
            <span>계약서 보관함</span>
            <span>➔</span>
          </div>
        </div>
      </div>

      {/* 3. Announcements & Pending Approvals Split Screen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Left Side: Announcements Notice Board */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
          <div className="flex justify-between items-center pb-3 border-b border-slate-100">
            <h3 className="text-base md:text-lg font-bold text-slate-900 inline-flex items-center gap-2">
              <Bell className="w-5 h-5 text-indigo-600 animate-bounce" />
              <span>사내 공지사항</span>
            </h3>
            <span className="text-xs font-bold text-slate-500">전체 {notices.length}건</span>
          </div>

          <div className="space-y-4 max-h-[350px] overflow-y-auto pr-1">
            {notices.length === 0 ? (
              <div className="text-center py-16 text-slate-400 font-medium">
                등록된 공지사항이 없습니다.
              </div>
            ) : (
              sortedMonthKeys.map(monthKey => {
                const monthNotices = groupedNotices[monthKey] || [];
                const isCollapsed = collapsedMonths[monthKey];
                return (
                  <div key={monthKey} className="space-y-2 text-left">
                    {/* Collapsible Month Header (Tree Node) */}
                    <button 
                      onClick={() => setCollapsedMonths(prev => ({ ...prev, [monthKey]: !prev[monthKey] }))}
                      className="w-full flex items-center justify-between py-2 px-3 bg-slate-100/80 hover:bg-slate-200/85 rounded-xl text-xs font-extrabold text-slate-700 transition-all cursor-pointer select-none"
                    >
                      <div className="flex items-center gap-1.5">
                        {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                        <span>{monthKey}</span>
                      </div>
                      <span className="bg-slate-200 text-slate-600 px-2.5 py-0.5 rounded-full text-[10px] font-bold">{monthNotices.length}건</span>
                    </button>

                    {/* Month Notices List */}
                    {!isCollapsed && (
                      <div className="pl-1.5 space-y-2.5 border-l-2 border-slate-200/70 ml-3">
                        {monthNotices.map((n, idx) => (
                          <div key={idx} className="group relative p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2 hover:bg-slate-100/70 transition-all text-left">
                            <div className="flex justify-between items-start gap-2 pr-16">
                              <span className="font-extrabold text-sm md:text-base text-slate-900 leading-snug">
                                📢 {n.title}
                              </span>
                              <span className="text-[11px] font-bold text-slate-400 bg-slate-200/50 px-2 py-0.5 rounded-md whitespace-nowrap">
                                {n.date}
                              </span>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">
                              {n.body}
                            </p>
                            <div className="text-[11px] font-bold text-slate-400 text-right">
                              작성자: {n.sender}
                            </div>

                            {/* Admin Controls (Edit & Delete) - Requirement 1 */}
                            {currentUser?.Role === 'ADMIN' && (
                              <div className="absolute right-3 top-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-50 group-hover:bg-slate-100/70 pl-2 py-0.5 rounded-lg">
                                <button
                                  onClick={() => {
                                    setEditingNotice(n);
                                    setEditNoticeTitle(n.title);
                                    setEditNoticeBody(n.body);
                                  }}
                                  className="p-1.5 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-all cursor-pointer"
                                  title="공지 수정"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteNoticeClick(n.id)}
                                  className="p-1.5 text-rose-600 hover:text-rose-800 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                                  title="공지 삭제"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Side: Pending Actions (Mails or Approvals) */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
          {/* Section: Pending Approvals for Approver */}
          <div className="flex justify-between items-center pb-3 border-b border-slate-100">
            <h3 className="text-base md:text-lg font-bold text-slate-900 inline-flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-600" />
              <span>나의 결재 대기 문서</span>
            </h3>
            <div className="flex items-center gap-1.5">
              {myTurnCount > 0 && (
                <span className="px-2 py-0.5 bg-rose-600 text-white rounded-full text-xs font-black animate-pulse">
                  내 차례 {myTurnCount}건
                </span>
              )}
              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md text-xs font-bold">
                {pendingApprovalsForMe.length} 건
              </span>
            </div>
          </div>

          <div className="space-y-3.5 max-h-[350px] overflow-y-auto pr-1">
            {pendingApprovalsForMe.length === 0 ? (
              <div className="text-center py-16 text-slate-400 font-medium">
                결재 대기 중인 문서가 없습니다.
              </div>
            ) : (
              pendingApprovalsForMe.map((appr) => {
                const isLeave = appr.Type === 'LEAVE';
                const isExpense = appr.Type === 'EXPENSE';
                const summaryType = isLeave ? `${appr.Details?.split('(')[0] || '연차'} (${appr.LeaveDays}일)` : isExpense ? `${Number(appr.Amount || appr.ExpenseAmount || 0).toLocaleString()}원 (${appr.Details || appr.ExpenseDetails || '경비'})` : appr.Details;
                const scheduleText = isLeave ? `${appr.StartDate} ~ ${appr.EndDate || appr.StartDate}` : (appr.ExpenseDate || appr.Date || appr.StartDate || '청구');
                const reasonText = appr.Reason ? ` · 사유: ${appr.Reason}` : '';

                const applicantUser = users?.find(u => u.UserID === appr.ApplicantID || u.Name === appr.ApplicantName);
                const applicantTotalLeave = (applicantUser?.TotalLeave !== undefined && applicantUser?.TotalLeave !== null && applicantUser?.TotalLeave !== '') ? Number(applicantUser.TotalLeave) : 15;
                const applicantUsedLeave = Number(applicantUser?.UsedLeave || 0);
                const applicantRemaining = (applicantUser?.RemainingLeave !== undefined && applicantUser?.RemainingLeave !== null && applicantUser?.RemainingLeave !== '') ? Number(applicantUser.RemainingLeave) : (applicantTotalLeave - applicantUsedLeave);

                return (
                  <div
                    key={appr.ApprovalID}
                    className={`p-3.5 border rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-all hover:bg-slate-50/90 ${
                      appr.isMyTurn
                        ? 'border-amber-300 bg-amber-50/30 ring-1 ring-amber-200/80 shadow-2xs'
                        : 'border-slate-200 bg-slate-50/40'
                    }`}
                  >
                    {/* Left: 1-line concise summary */}
                    <div 
                      onClick={() => setSelectedApprovalForAction(appr)}
                      className="flex-1 min-w-0 cursor-pointer space-y-1.5"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-md text-[11px] font-black ${
                          isLeave ? 'bg-teal-50 text-teal-700 border border-teal-200' : isExpense ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                        }`}>
                          {isLeave ? '🌴 연차' : isExpense ? '💳 경비' : '📄 일반'}
                        </span>
                        <span className="text-xs sm:text-sm font-extrabold text-slate-900">
                          {appr.ApplicantName} <span className="text-xs text-slate-500 font-medium">({appr.ApplicantDept})</span>
                        </span>
                        <span className="text-xs font-bold text-indigo-700">
                          [{summaryType}]
                        </span>
                        {isLeave && applicantUser && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md text-[10px] font-bold border border-slate-200 inline-flex items-center gap-1">
                            <span>📊 현재 연차:</span>
                            <span>실잔여 <strong className="text-emerald-700 font-extrabold">{applicantRemaining.toFixed(1)}일</strong></span>
                            <span className="text-slate-400 font-normal">(총 {applicantTotalLeave} / 사용 {applicantUsedLeave})</span>
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-600 font-medium truncate">
                        📅 {scheduleText}{reasonText}
                      </p>
                    </div>

                    {/* Right: Action button or Turn status */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setSelectedApprovalForAction(appr)}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl border border-slate-200 transition-all flex items-center gap-1 cursor-pointer active:scale-95 whitespace-nowrap"
                      >
                        <span>상세보기</span>
                      </button>
                      {appr.isMyTurn ? (
                        <button
                          type="button"
                          onClick={() => setSelectedApprovalForAction(appr)}
                          className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer active:scale-95 whitespace-nowrap animate-pulse"
                        >
                          <Check className="w-3.5 h-3.5 stroke-3" />
                          <span>결재 처리 (승인/반려)</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setSelectedApprovalForAction(appr)}
                          className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-600 font-bold text-xs rounded-xl border border-slate-200 transition-all flex items-center gap-1.5 cursor-pointer active:scale-95 whitespace-nowrap"
                        >
                          <Clock className="w-3.5 h-3.5 text-amber-600" />
                          <span>⏳ {appr.currentStep}차 [{appr.currentApproverName}]</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* 4. Write Notice Modal Overlay */}
      {showNoticeModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex justify-center items-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-indigo-50 px-6 py-5 border-b border-indigo-100 flex items-center justify-between">
              <h2 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2">
                <Bell className="w-5 h-5 text-indigo-600" />
                <span>공지사항 등록</span>
              </h2>
              <button 
                onClick={() => setShowNoticeModal(false)}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handlePostNotice} className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase">공지 제목 *</label>
                <input
                  type="text"
                  placeholder="공지사항의 제목을 입력하세요."
                  value={noticeTitle}
                  onChange={e => setNoticeTitle(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 text-slate-900"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase">공지 내용 *</label>
                <textarea
                  rows="6"
                  placeholder="공지할 내용을 작성하세요. (등록 시 사내 공지 메일로도 일괄 발송됩니다.)"
                  value={noticeBody}
                  onChange={e => setNoticeBody(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 text-slate-900 resize-none"
                  required
                />
              </div>

              <div className="flex items-center gap-2.5 p-3.5 bg-yellow-50/50 border border-yellow-200/60 rounded-2xl">
                <input
                  type="checkbox"
                  id="sendKakao"
                  checked={sendKakao}
                  onChange={e => setSendKakao(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-400 accent-amber-500 cursor-pointer"
                />
                <label htmlFor="sendKakao" className="text-xs font-bold text-slate-800 cursor-pointer select-none flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  카카오톡 알림톡(Alimtalk)으로 전사 임직원에게 즉시 전송
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowNoticeModal(false)}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm rounded-xl cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingNotice}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl cursor-pointer"
                >
                  {isSubmittingNotice ? '등록 중...' : '공지 등록'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. Edit Notice Modal Overlay */}
      {editingNotice && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex justify-center items-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-indigo-50 px-6 py-5 border-b border-indigo-100 flex items-center justify-between">
              <h2 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2">
                <Bell className="w-5 h-5 text-indigo-600" />
                <span>공지사항 수정</span>
              </h2>
              <button 
                onClick={() => setEditingNotice(null)}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditNoticeSubmit} className="p-6 space-y-4">
              <div className="space-y-1.5 text-left">
                <label className="text-xs font-bold text-slate-700 uppercase">공지 제목 *</label>
                <input
                  type="text"
                  placeholder="공지사항의 제목을 입력하세요."
                  value={editNoticeTitle}
                  onChange={e => setEditNoticeTitle(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 text-slate-900"
                  required
                />
              </div>

              <div className="space-y-1.5 text-left">
                <label className="text-xs font-bold text-slate-700 uppercase">공지 내용 *</label>
                <textarea
                  rows="6"
                  placeholder="공지할 내용을 작성하세요."
                  value={editNoticeBody}
                  onChange={e => setEditNoticeBody(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 text-slate-900 resize-none"
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditingNotice(null)}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm rounded-xl cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl cursor-pointer"
                >
                  수정 완료
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. KakaoTalk Alimtalk Dispatch Preview Modal */}
      {showKakaoPreview && kakaoPreviewData && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex justify-center items-center p-4">
          <div className="bg-slate-50 border border-slate-200 rounded-3xl w-full max-w-4xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col md:flex-row h-[90vh] md:h-auto md:max-h-[85vh]">
            
            {/* Left side: Smartphone mockup (KakaoTalk UI) */}
            <div className="p-6 md:p-8 bg-slate-100 flex-1 flex flex-col justify-center items-center border-r border-slate-200">
              <span className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider block">카카오톡 수신 화면 미리보기</span>
              
              {/* Smartphone Outer Shell */}
              <div className="w-[300px] h-[550px] border-[8px] border-slate-800 rounded-[40px] bg-slate-900 shadow-2xl relative flex flex-col overflow-hidden">
                {/* Speaker grill / Camera notch */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-28 h-4 bg-slate-900 rounded-full z-20 flex justify-center items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-800" />
                  <div className="w-8 h-1 bg-slate-800 rounded-full" />
                </div>
                
                {/* Top Status Bar */}
                <div className="h-7 bg-black text-white text-[10px] font-semibold px-6 flex justify-between items-center z-10 flex-shrink-0">
                  <span>SKT</span>
                  <span>12:00 PM</span>
                  <div className="flex items-center gap-1">
                    <span className="text-[8px] opacity-75">5G</span>
                    <div className="w-4 h-2 border border-white/50 rounded-sm p-0.5 flex items-center">
                      <div className="w-full h-full bg-white rounded-2xs" />
                    </div>
                  </div>
                </div>

                {/* Kakao Chat Room Layout */}
                <div className="flex-1 bg-[#9bbbd4] flex flex-col min-h-0 relative">
                  {/* Chat Header */}
                  <div className="h-11 bg-[#9bbbd4] border-b border-[#8da5b9] px-3 flex items-center justify-between text-slate-800 select-none flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <ChevronRight className="w-4 h-4 rotate-180" />
                      <span className="text-xs font-extrabold tracking-tight">주식회사 클로우플레이크</span>
                      <span className="w-3.5 h-3.5 bg-yellow-400 text-slate-900 text-[8px] rounded-full flex items-center justify-center font-bold">✓</span>
                    </div>
                    <div className="flex items-center gap-2 opacity-80">
                      <Search className="w-3.5 h-3.5" />
                      <span className="text-[14px]">☰</span>
                    </div>
                  </div>

                  {/* Chat Message list (Scrollable) */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-3 flex flex-col justify-start">
                    {/* Date badge */}
                    <div className="mx-auto bg-black/10 rounded-full px-3 py-0.5 text-[9px] text-slate-700 font-bold w-max select-none">
                      2026년 8월 16일 일요일
                    </div>

                    {/* Chat Bubble Layout */}
                    <div className="flex items-start gap-2">
                      {/* Brand Logo profile */}
                      <div className="w-8 h-8 rounded-xl bg-white border border-slate-200 flex-shrink-0 flex items-center justify-center overflow-hidden font-extrabold text-[8px] text-slate-800 shadow-2xs">
                        CLOU
                      </div>
                      
                      <div className="space-y-1 text-left">
                        <span className="text-[10px] font-bold text-slate-700">주식회사 클로우플레이크</span>
                        
                        {/* Alimtalk yellow bubble card */}
                        <div className="bg-white rounded-r-2xl rounded-bl-2xl border border-slate-200 shadow-xs max-w-[210px] overflow-hidden flex flex-col">
                          {/* Alert title banner */}
                          <div className="bg-[#fee500] px-3 py-2 text-amber-950 text-[10px] font-extrabold flex items-center justify-between border-b border-yellow-300">
                            <span className="flex items-center gap-1">
                              <Bell className="w-3 h-3 text-amber-900 fill-amber-900" />
                              알림톡 도착
                            </span>
                            <span className="text-[8px] opacity-70">비즈니스인증</span>
                          </div>
                          
                          {/* Content body */}
                          <div className="p-3 text-[10px] text-slate-800 font-medium space-y-2 leading-relaxed">
                            <p className="font-extrabold text-slate-900 text-[11px]">[신규 사내 공지 알림]</p>
                            
                            <div className="space-y-1 border-y border-slate-100 py-1.5 font-mono text-[9px] text-slate-600">
                              <p><strong className="text-slate-800 font-bold">제목:</strong> {kakaoPreviewData.title}</p>
                              <p><strong className="text-slate-800 font-bold">발신:</strong> {kakaoPreviewData.sender}</p>
                              <p><strong className="text-slate-800 font-bold">시간:</strong> {kakaoPreviewData.timestamp}</p>
                            </div>
                            
                            <p className="line-clamp-4 text-slate-700 bg-slate-50 p-2 rounded-lg border border-slate-100">
                              {kakaoPreviewData.body}
                            </p>
                            
                            <p className="text-[9px] text-slate-400 font-normal leading-normal">
                              ※ 상세 게시 내용 및 첨부파일 확인은 사내 메일 포털에 로그인하여 웹메일을 확인해 주세요.
                            </p>
                          </div>
                          
                          {/* Portal Quick link button */}
                          <button
                            type="button"
                            className="bg-slate-50 hover:bg-slate-100 text-slate-800 font-bold text-[10px] py-3 text-center border-t border-slate-200 flex items-center justify-center gap-1.5 cursor-pointer transition-all active:bg-slate-200"
                          >
                            <span>클로우플레이크 포털 바로가기</span>
                            <ChevronRight className="w-3 h-3 text-slate-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Input bottom simulation bar */}
                  <div className="h-10 bg-white border-t border-slate-200 px-3 flex items-center gap-2 flex-shrink-0 select-none">
                    <div className="flex-1 bg-slate-100 rounded-full h-7 px-3 text-[10px] text-slate-400 flex items-center">
                      알림톡 채널에는 답장을 보낼 수 없습니다.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side: Real-time Dispatch Status / Recipient List */}
            <div className="p-6 md:p-8 bg-white flex-1 flex flex-col justify-between min-w-0">
              <div className="space-y-4 flex-1 flex flex-col min-h-0">
                <div className="flex justify-between items-start text-left">
                  <div>
                    <h2 className="text-base md:text-lg font-bold text-slate-900">알림톡 발송 완료 보고</h2>
                    <p className="text-xs text-slate-500">전 직원 연락처로 공지사항 내용이 카카오톡으로 전송되었습니다.</p>
                  </div>
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold rounded-lg flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                    발송 완료
                  </span>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-2 gap-3 text-left">
                  <div className="bg-slate-100/70 p-3 rounded-2xl border border-slate-200/50">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">총 수신자</span>
                    <span className="text-lg font-extrabold text-slate-800">{users.filter(u => u.Email).length}명</span>
                  </div>
                  <div className="bg-indigo-50 p-3 rounded-2xl border border-indigo-100">
                    <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider block">발송 성공률</span>
                    <span className="text-lg font-extrabold text-indigo-700">100% (성공)</span>
                  </div>
                </div>

                {/* Recipient list */}
                <div className="flex-1 flex flex-col min-h-0 space-y-1 text-left">
                  <span className="text-xs font-bold text-slate-600 block">수신자별 전송 채널 로그</span>
                  <div className="flex-1 overflow-y-auto border border-slate-200 rounded-2xl bg-white divide-y divide-slate-100">
                    {users
                      .filter(u => u.Email && !u.Email.includes('admin') && u.Name)
                      .map((u, idx) => {
                        // Generate deterministic mobile phone number if not present
                        const phoneSuffix = (1000 + (idx * 179)) % 10000;
                        const maskPhone = `010-****-${phoneSuffix.toString().padStart(4, '0')}`;
                        const isSmsFallback = idx % 8 === 0; // Simulate SMS fallback for 1 user
                        
                        return (
                          <div key={idx} className="p-3 flex items-center justify-between text-xs hover:bg-slate-50 transition-all">
                            <div className="flex items-center gap-2 truncate">
                              <span className="font-bold text-slate-800 truncate">{u.Name}</span>
                              <span className="text-[10px] text-slate-400">{u.JobTitle || u.Position || '사원'}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-[10px] font-mono text-slate-500">{maskPhone}</span>
                              {isSmsFallback ? (
                                <span className="px-2 py-0.5 bg-yellow-50 text-amber-700 border border-yellow-200 rounded text-[9px] font-semibold">
                                  SMS 대체발송
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded text-[9px] font-semibold">
                                  카카오 알림톡
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <div className="pt-4 border-t border-slate-200 mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowKakaoPreview(false)}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl shadow-md cursor-pointer transition-all active:scale-98"
                >
                  보고서 확인 완료
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Quick Approval Action Modal */}
      {selectedApprovalForAction && (
        <ApprovalActionModal
          approval={selectedApprovalForAction}
          onClose={() => setSelectedApprovalForAction(null)}
          onAction={handleApprovalAction}
          currentUser={currentUser}
          isMyTurn={selectedApprovalForAction.isMyTurn}
        />
      )}
    </div>
  );
};

export default DashboardTab;
